import { SuggestionList, SuggestionSource } from 'services/food-suggest-client';
import { SuggestionRow } from '@cf-shared/food-search/food-suggestion-row';

/**
 * Sends analytics events; in the app, `TelemetryService` itself. Handed over whole rather than as a
 * forwarding function, so every event name below stays a string literal at its call, where the
 * telemetry lexicon gate (scripts/ci/check-telemetry-lexicon.mjs) can read it.
 */
export interface SuggestionTelemetry {
  trackEvent: (eventName: string, params: { [key: string]: unknown }) => void;
}

/** What the Foods page must do after a key was pressed in the search box. */
export enum SuggestionKeyActionKind {
  /** nothing beyond what `preventDefault` says */
  None = 'none',
  /** the highlighted row was chosen with Enter: put its line in the box and search it */
  PickRow = 'pickRow',
  /** Enter with no row highlighted: search exactly what was typed */
  CommitTypedText = 'commitTypedText',
}

export type SuggestionKeyAction =
  | { kind: SuggestionKeyActionKind.None, preventDefault: boolean }
  | { kind: SuggestionKeyActionKind.PickRow, preventDefault: true, row: SuggestionRow, position: number }
  | { kind: SuggestionKeyActionKind.CommitTypedText, preventDefault: true };

/**
 * The suggestion rows under the Foods search box (#5410): which rows are on screen, whether they
 * are visible, which one is highlighted, the keys that move and choose, and the analytics events
 * that say what the person did with them.
 *
 * `FoodSuggestController` decides WHAT to suggest for the typed text; this decides how the rows
 * behave once they are on screen. The page keeps what is really its own: feeding keystrokes to the
 * controller, committing a search, and spelling corrections (df-5a63574c1598).
 *
 * A plain class rather than an Angular component, so it can be unit tested: an Angular class
 * cannot be loaded by the mocha unit-test run. The page hands in the telemetry service.
 */
export class FoodSuggestionRows {
  /** the rows to show and where they came from; undefined when none have arrived */
  list: SuggestionList | undefined = undefined;
  /** the row the arrow keys have moved to, or -1 when none is; Enter picks it */
  highlightedRow = -1;
  /** true while the server is being asked, which turns the search box's icon into a spinner */
  pending = false;
  /** false once a line is committed, Esc is pressed or the box loses focus; typing shows them again */
  private visible = false;
  /**
   * Whether rows have been reported as shown since they last went away. Reset whenever the rows
   * are hidden -- by a commit, Esc, the box losing focus, or the page being left -- so each
   * `foods_suggestions_shown` pairs with at most one `picked` or `abandoned`.
   */
  private shownReported = false;

  /**
   * @param telemetry sends the analytics events
   */
  constructor(private telemetry: SuggestionTelemetry) {}

  /** Are suggestion rows on screen right now? */
  get rowsShown(): boolean {
    return this.visible && (this.list?.rows.length ?? 0) > 0;
  }

  /**
   * Is the suggestion list OPEN, whether or not it has anything in it?
   *
   * The difference from `rowsShown` is the case where the list is open and empty — nothing matched
   * what was typed. A caller deciding whether it may put a NEW list up needs this one: `rowsShown`
   * cannot tell an open-but-empty list from one the person deliberately closed, and answering the
   * second question with the first means never filling in an empty list.
   */
  get listOpen(): boolean {
    return this.visible;
  }

  /**
   * Is the suggestion list open with only a spinner in it? While suggestions for the typed text
   * are on their way and no rows are on screen yet. On a slow connection the rows can take a
   * second or more, and without this the person sees nothing and may commit the search before
   * any row arrives. Rows already on screen stay while the next list loads instead (#5410).
   */
  get loadingShown(): boolean {
    return this.visible && this.pending && !this.rowsShown;
  }

  /**
   * The person typed words: show the rows again with none highlighted. Rows kept from before an
   * Esc or a blur come back into view at once, so they count as shown now, not when the next list
   * arrives.
   */
  typingResumed() {
    this.visible = true;
    this.highlightedRow = -1;
    this.reportShownOnce();
  }

  /** A new list of rows arrived for the typed text. */
  showList(list: SuggestionList) {
    this.list = list;
    this.highlightedRow = -1;
    this.reportShownOnce();
  }

  /** Take the rows off the screen and forget them, reporting nothing. */
  clear() {
    this.shownReported = false;
    this.list = undefined;
    this.visible = false;
    this.pending = false;
    this.highlightedRow = -1;
  }

  /**
   * Report rows left on screen with none picked, without hiding them. Called when the page is
   * left; `hideUnpicked` covers Esc and the box losing focus.
   *
   * @param typedText the text in the search box
   */
  reportAbandonedIfShown(typedText: string) {
    if (this.rowsShown) {
      this.telemetry.trackEvent('foods_suggestions_abandoned', {
        source: this.list.source, typedLength: typedText.length, searchText: typedText,
      });
    }
  }

  /** The search box lost focus: rows still on screen with none picked are abandoned. */
  searchBoxBlurred(typedText: string) {
    this.hideUnpicked(typedText);
  }

  /**
   * A key was pressed in the search box. Enter -- also the phone keyboard's
   * Search key -- runs the highlighted row, or exactly what was typed when none is highlighted: a
   * suggestion is never applied without a keypress choosing it. The arrow keys move the
   * highlight, Esc closes the rows.
   *
   * @param key `KeyboardEvent.key`
   * @param typedText the text in the search box, reported if Esc abandons the rows
   */
  keyPressed(key: string, typedText: string): SuggestionKeyAction {
    const rowCount = this.rowsShown ? this.list.rows.length : 0;
    switch (key) {
      case 'Enter':
        if (this.highlightedRow >= 0 && this.highlightedRow < rowCount) {
          const position = this.highlightedRow;
          return { kind: SuggestionKeyActionKind.PickRow, preventDefault: true, row: this.list.rows[position], position };
        }
        return { kind: SuggestionKeyActionKind.CommitTypedText, preventDefault: true };
      case 'ArrowDown':
        if (rowCount > 0) {
          this.highlightedRow = (this.highlightedRow + 1) % rowCount;
          return { kind: SuggestionKeyActionKind.None, preventDefault: true };
        }
        break;
      case 'ArrowUp':
        if (rowCount > 0) {
          this.highlightedRow = this.highlightedRow <= 0 ? rowCount - 1 : this.highlightedRow - 1;
          return { kind: SuggestionKeyActionKind.None, preventDefault: true };
        }
        break;
      case 'Escape':
        // closes the spinner-only list too, so rows arriving afterwards stay hidden until the next key
        if (this.rowsShown || this.loadingShown) {
          this.hideUnpicked(typedText);
          return { kind: SuggestionKeyActionKind.None, preventDefault: true };
        }
        break;
    }
    return { kind: SuggestionKeyActionKind.None, preventDefault: false };
  }

  /**
   * Report that a row was picked, by tapping it or with Enter on the highlighted one. The page
   * then puts the line in the box and commits the search, which clears the rows.
   *
   * @param typedText the text in the search box before the pick replaced it
   */
  reportPicked(row: SuggestionRow, position: number, typedText: string) {
    this.telemetry.trackEvent('foods_suggestion_picked', {
      source: this.list?.source ?? SuggestionSource.Server,
      position,
      typedLength: typedText.length,
      hasCount: row.count !== undefined,
      isStaple: row.stapleId !== undefined,
    });
  }

  /**
   * Report `foods_suggestions_shown` if rows are on screen and have not been reported since they
   * last went away -- once, however often the list changes while the person types on. Called
   * wherever rows can come into view, so a pick or an abandonment never comes before it.
   */
  private reportShownOnce() {
    if (this.rowsShown && !this.shownReported) {
      this.shownReported = true;
      this.telemetry.trackEvent('foods_suggestions_shown', {
        source: this.list.source, typedLength: this.list.forText.length,
      });
    }
  }

  /** Hide the rows with none picked (Esc, or the box losing focus): an abandoned list. */
  private hideUnpicked(typedText: string) {
    this.reportAbandonedIfShown(typedText);
    this.visible = false;
    this.shownReported = false;
    this.highlightedRow = -1;
  }
}
